import { Observable, Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

// Borrowed from https://blog.bitsrc.io/angular-maximizing-performance-with-the-intersection-observer-api-23d81312f178
// But adapted to not disconnect as soon as the element becomes visible.

export enum IntersectionStatus {
  Visible = 'Visible',
  Pending = 'Pending',
  NotVisible = 'NotVisible'
}

/**
 * Takes an HTML element, runs it thru an intersection observer and returns an Rxjs observable
 * of its current `IntersectionStatus`.
 * @param element
 * @param config
 * @param debounce
 */
export const fromIntersectionObserver = (
    element: HTMLElement,
    config: IntersectionObserverInit,
    debounce = 250,
): Observable<IntersectionStatus> => {
  return new Observable<IntersectionStatus>(subscriber => {
    const subject$ = new Subject<{
      entry: IntersectionObserverEntry;
      observer: IntersectionObserver;
    }>();

    const intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach(entry => {
            subject$.next({ entry, observer });
          });
        },
        config,
    );

    subject$.subscribe(() => {
      subscriber.next(IntersectionStatus.Pending);
    });

    subject$
        .pipe(
            debounceTime(debounce),
            filter(Boolean),
        )
        .subscribe(({ entry, observer }) => {
          const isEntryVisible = entry.isIntersecting;

          if (isEntryVisible) {
            subscriber.next(IntersectionStatus.Visible);
          } else {
            subscriber.next(IntersectionStatus.NotVisible);
          }
        });

    intersectionObserver.observe(element);

    return {
      unsubscribe() {
        intersectionObserver.disconnect();
        subject$.unsubscribe();
      },
    };
  });
};